import { useState, useEffect } from "react";
import { getMyRestaurant } from "../services/restaurantService";
import {
  getCategories,
  createCategory,
  getIngredients,
  createFood,
  deleteFood,
  toggleFoodAvailability,
} from "../services/foodService";
import { getRestaurantFoods } from "../services/restaurantService";

export default function FoodManagementPage() {
  const [restaurant, setRestaurant] = useState(null);
  const [foods, setFoods]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  const [newCatName, setNewCatName] = useState("");

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    vegetarian: false,
    seasonal: false,
    categoryId: "",
    categoryName: "",
    selectedIngredients: [],
    images: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href = "/"; return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const rest = await getMyRestaurant();
      setRestaurant(rest);
      const [f, cats, ings] = await Promise.all([
        getRestaurantFoods(rest.id),
        getCategories(),
        getIngredients(rest.id),
      ]);
      setFoods(f);
      setCategories(cats);
      setIngredients(ings);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleIngredientToggle = (id) => {
    setForm(f => ({
      ...f,
      selectedIngredients: f.selectedIngredients.includes(id)
        ? f.selectedIngredients.filter(i => i !== id)
        : [...f.selectedIngredients, id],
    }));
  };

  const handleAddFood = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const selectedCat = categories.find(c => c.id === parseInt(form.categoryId));
      const foodData = {
        name: form.name,
        description: form.description,
        price: parseInt(form.price),
        vegetarian: form.vegetarian,
        seasional: form.seasonal,
        restaurentId: restaurant.id,
        category: { id: selectedCat.id, name: selectedCat.name },
        images: form.images ? [form.images] : [],
        ingredients: form.selectedIngredients.map(id => ({ id })),
      };
      const newFood = await createFood(foodData);
      setFoods(prev => [...prev, newFood]);
      setShowAddModal(false);
      setForm({ name: "", description: "", price: "", vegetarian: false, seasonal: false, categoryId: "", categoryName: "", selectedIngredients: [], images: "" });
      showSuccess("Food item add ho gaya! ✅");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete karna chahte ho?")) return;
    try {
      await deleteFood(id);
      setFoods(prev => prev.filter(f => f.id !== id));
      showSuccess("Food delete ho gaya! 🗑️");
    } catch (e) { setError(e.message); }
  };

  const handleToggle = async (id) => {
    try {
      const updated = await toggleFoodAvailability(id);
      setFoods(prev => prev.map(f => f.id === id ? updated : f));
    } catch (e) { setError(e.message); }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cat = await createCategory(newCatName);
      setCategories(prev => [...prev, cat]);
      setNewCatName("");
      setShowCatModal(false);
      showSuccess("Category ban gayi! ✅");
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">

      {/* Navbar */}
      <nav className="bg-[#1a1a1a] border-b border-zinc-800 px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🍽️</span>
          <span className="text-xl font-black">Sav<span className="text-orange-500">ora</span></span>
          <span className="text-zinc-600 mx-2">|</span>
          <span className="text-zinc-400 text-sm">{restaurant?.name}</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => window.location.href = "/dashboard"}
            className="text-xs text-zinc-400 hover:text-white transition-colors">
            ← Dashboard
          </button>
          <button onClick={() => { localStorage.clear(); window.location.href = "/"; }}
            className="text-xs text-zinc-500 hover:text-red-400 transition-colors">
            Logout →
          </button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* Alerts */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-4">
            <p className="text-red-400 text-sm">⚠️ {error}</p>
          </div>
        )}
        {success && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 mb-4">
            <p className="text-green-400 text-sm">{success}</p>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white">Menu Management</h1>
            <p className="text-zinc-500 text-sm mt-1">{foods.length} items · {categories.length} categories</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowCatModal(true)}
              className="text-sm border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-4 py-2 rounded-xl transition-colors">
              + Category
            </button>
            <button onClick={() => setShowAddModal(true)}
              className="text-sm bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-xl transition-colors">
              + Add Food
            </button>
          </div>
        </div>

        {/* Categories chips */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map(cat => (
              <span key={cat.id} className="bg-zinc-800 text-zinc-300 text-xs px-3 py-1.5 rounded-full border border-zinc-700">
                {cat.name}
              </span>
            ))}
          </div>
        )}

        {/* Food Grid */}
        {foods.length === 0 ? (
          <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-16 text-center">
            <div className="text-6xl mb-4">🍽️</div>
            <h2 className="text-xl font-bold text-white mb-2">Menu Khali Hai</h2>
            <p className="text-zinc-500 text-sm mb-6">Pehla food item add karo!</p>
            <button onClick={() => setShowAddModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors">
              + Add Food Item
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {foods.map(food => (
              <div key={food.id} className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl overflow-hidden group">
                {/* Image */}
                <div className="relative h-44 bg-zinc-900">
                  {food.images?.[0] ? (
                    <img src={food.images[0]} alt={food.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl">🍱</div>
                  )}
                  {/* Overlay buttons */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <button onClick={() => handleToggle(food.id)}
                      className={`text-xs font-bold px-3 py-2 rounded-lg transition-colors ${
                        food.available
                          ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                          : "bg-green-500 hover:bg-green-600 text-white"
                      }`}>
                      {food.available ? "Unavailable" : "Available"}
                    </button>
                    <button onClick={() => handleDelete(food.id)}
                      className="text-xs font-bold px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors">
                      Delete
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-bold text-white text-sm">{food.name}</h3>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ml-2 shrink-0 ${
                      food.vegetarian ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
                    }`}>
                      {food.vegetarian ? "🟢 Veg" : "🔴 Non"}
                    </span>
                  </div>
                  <p className="text-zinc-500 text-xs mb-3 line-clamp-2">{food.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-orange-400 font-black text-lg">₹{food.price}</span>
                    <span className={`text-xs font-medium ${food.available ? "text-green-400" : "text-zinc-600"}`}>
                      {food.available ? "● Available" : "● Unavailable"}
                    </span>
                  </div>
                  {food.foodCategory && (
                    <span className="inline-block mt-2 bg-zinc-800 text-zinc-400 text-xs px-2 py-0.5 rounded-full">
                      {food.foodCategory.name}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Add Food Modal ─────────────────────────── */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <h2 className="text-lg font-black text-white">New Food Item</h2>
              <button onClick={() => setShowAddModal(false)} className="text-zinc-500 hover:text-white text-xl">✕</button>
            </div>

            <form onSubmit={handleAddFood} className="p-6 space-y-4">

              {/* Name */}
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Name *</label>
                <input type="text" name="name" value={form.name} onChange={handleFormChange} required
                  placeholder="Chicken Biryani"
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Description</label>
                <textarea name="description" value={form.description} onChange={handleFormChange} rows={2}
                  placeholder="Aromatic basmati rice with tender chicken..."
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors resize-none" />
              </div>

              {/* Price */}
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Price (₹) *</label>
                <input type="number" name="price" value={form.price} onChange={handleFormChange} required min="1"
                  placeholder="299"
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
              </div>

              {/* Category */}
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Category *</label>
                {categories.length === 0 ? (
                  <div className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-500">
                    Pehle category banao →{" "}
                    <button type="button" onClick={() => { setShowAddModal(false); setShowCatModal(true); }}
                      className="text-orange-400 underline">Category banao</button>
                  </div>
                ) : (
                  <select name="categoryId" value={form.categoryId} onChange={handleFormChange} required
                    className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500 transition-colors">
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Image URL */}
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Image URL</label>
                <input type="url" name="images" value={form.images} onChange={handleFormChange}
                  placeholder="https://images.pexels.com/..."
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
              </div>

              {/* Ingredients */}
              {ingredients.length > 0 && (
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-wider mb-2 block">Ingredients</label>
                  <div className="flex flex-wrap gap-2">
                    {ingredients.map(ing => (
                      <button type="button" key={ing.id}
                        onClick={() => handleIngredientToggle(ing.id)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          form.selectedIngredients.includes(ing.id)
                            ? "bg-orange-500 border-orange-500 text-white"
                            : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                        }`}>
                        {ing.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Veg / Seasonal toggles */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="vegetarian" checked={form.vegetarian} onChange={handleFormChange}
                    className="w-4 h-4 accent-green-500" />
                  <span className="text-sm text-zinc-300">🟢 Vegetarian</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="seasonal" checked={form.seasonal} onChange={handleFormChange}
                    className="w-4 h-4 accent-orange-500" />
                  <span className="text-sm text-zinc-300">🌿 Seasonal</span>
                </label>
              </div>

              {error && <p className="text-red-400 text-sm">⚠️ {error}</p>}

              <button type="submit" disabled={saving}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors">
                {saving ? "Saving..." : "Add Food Item →"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Add Category Modal ─────────────────────── */}
      {showCatModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <h2 className="text-lg font-black text-white">New Category</h2>
              <button onClick={() => setShowCatModal(false)} className="text-zinc-500 hover:text-white text-xl">✕</button>
            </div>
            <form onSubmit={handleAddCategory} className="p-6 space-y-4">
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Category Name *</label>
                <input type="text" value={newCatName} onChange={e => setNewCatName(e.target.value)} required
                  placeholder="Biryani, Curry, Dessert..."
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
              </div>
              <button type="submit" disabled={saving}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors">
                {saving ? "Saving..." : "Create Category →"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}